import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const RondoInEFlatViewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Rondo in E-flat Major
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachCPE/cpe-bach-rondo/cpe-bach-rondo-let.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'C.P.E. Bach - Rondo in E-flat Major.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-6151">
      <Navbar data-id="element-6152" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-6153">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-6154">
          <img src="https://images.unsplash.com/photo-1519683109079-d5f539e1542f?w=1200&h=400&fit=crop" alt="Rondo in E-flat Major" className="w-full h-64 md:h-80 object-cover" data-id="element-6155" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-6156" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-6157">
            <div className="max-w-7xl mx-auto" data-id="element-6158">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-6159">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-6160" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-6161">
                Rondo in E-flat Major
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-6162">
                For Harpsichord
              </p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-6163">
                Carl Philipp Emanuel Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-6164">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-6165">
          <div className="flex items-center space-x-2" data-id="element-6166">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-6167">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-6168" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6169">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-6170">
                  <ZoomInIcon className="h-5 w-5" data-id="element-6171" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-6172">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-6173">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-6174" />
              <span className="hidden sm:inline" data-id="element-6175">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-6176">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-6177" />
              <span className="hidden sm:inline" data-id="element-6178">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-6179">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-6180">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-6181">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Rondo in E-flat Major Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-6182" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-6183">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6184">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-6185">
            <div data-id="element-6186">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6187">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-6188">
                <div data-id="element-6189">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6190">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6191">
                    Carl Philipp Emanuel Bach
                  </dd>
                </div>
                <div data-id="element-6192">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6193">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6194">
                    1780
                  </dd>
                </div>
                <div data-id="element-6195">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6196">
                    Key
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6197">
                    E♭ Major
                  </dd>
                </div>
                <div data-id="element-6198">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6199">
                    Instrument
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6200">
                    Harpsichord
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-6201">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6202">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-6203">
                The Rondo in E-flat Major is a charming example of Carl Philipp
                Emanuel Bach's keyboard writing, showcasing the galant style
                that bridged the Baroque and Classical periods. As the second
                son of Johann Sebastian Bach, C.P.E. Bach developed a highly
                expressive and innovative keyboard style that influenced
                composers like Haydn, Mozart, and Beethoven. This rondo features
                elegant melodic lines, surprising harmonic turns, and the
                characteristic empfindsamer Stil (sensitive style) for which
                C.P.E. Bach was renowned. The piece demonstrates the composer's
                mastery of form and his ability to create music that is both
                intellectually satisfying and emotionally engaging.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-6204" />
    </div>;
};